import { Injectable } from '@angular/core';
import { InternalDatabaseService } from '../internal/internal-database.service';
import { ChecklistProgress } from './checklist-progress';
import { Observable, BehaviorSubject } from 'rxjs';
import { SettingsService } from '@stockcare-services/settings-service/settings.service';
import { Settings } from '@stockcare-helpers/settings';

@Injectable({
  providedIn: 'root',
})
export class ChecklistProgressStorageService {
  readonly table: string;
  readonly Index: string;
  enableLocaldb: boolean;
  checklistProgress: ChecklistProgress[] = [];

  private checklistProgressUpdated = new BehaviorSubject<{ patientTheatreVisitId: number; progress?: ChecklistProgress }>({ patientTheatreVisitId: -1 });
  public checklistProgressUpdated$ = this.checklistProgressUpdated.asObservable();

  constructor(
    private internalDatabaseService: InternalDatabaseService,
    private readonly settingsService: SettingsService
  ) {
    const table = ChecklistProgress.getTableDefinition();
    this.table = table.TableName;
    this.Index = table.Indexes[0].Name;
  }

  getSettings() {
    this.enableLocaldb = this.settingsService.getBooleanSetting(Settings.ENABLE_INDEXED_DB);
  }

  addUpdateChecklistProgress(patientTheatreVisitId: number, progressString: string): Observable<boolean> {
    this.getSettings();

    return new Observable<boolean>((observer) => {
      if (!progressString || typeof progressString !== 'string') {
        observer.next(false);
        observer.complete();
        return;
      }

      this.getChecklistProgressById(patientTheatreVisitId).subscribe({
        next: async (existingProgress) => {
          try {
            if (existingProgress) {
              const existingChecklistProgress = new ChecklistProgress();
              existingChecklistProgress.Id = patientTheatreVisitId;
              existingChecklistProgress.Progress = progressString;
              await this.updateProgress(existingChecklistProgress);
              observer.next(true);
            } else {
              const checklistProgress = new ChecklistProgress();
              checklistProgress.Id = patientTheatreVisitId;
              checklistProgress.Progress = progressString;

              await this.addProgress(checklistProgress);
              observer.next(true);
            }
            observer.complete();
          } catch (error) {
            console.error('Error in addUpdateChecklistProgress:', error);
            observer.error(error);
          }
        },
        error: (error) => {
          console.error('Error getting existing progress:', error);
          observer.error(error);
        }
      });
    });
  }

  getChecklistProgressById(id: number): Observable<string | null> {
    this.getSettings();

    return new Observable((observer) => {
      if (this.enableLocaldb) {
        this.internalDatabaseService.getByIndex<ChecklistProgress>(this.table, this.Index, id).then((progress) => {
          observer.next(progress.Progress || null);
          observer.complete();
        }).catch((error) => {
          observer.next(undefined);
          observer.complete();
        });
      } else {
        const progress = this.checklistProgress.find(progress => progress.Id === id);
        observer.next(progress ? progress.Progress : null);
        observer.complete();
      }
    });
  }

  async addProgress(progress: ChecklistProgress) {
    if (this.enableLocaldb) {
      return await this.internalDatabaseService.add(this.table, progress);
    } else {
      const existingIndex = this.checklistProgress.findIndex(p => p.Id === progress.Id);
      if (existingIndex !== -1) {
        this.checklistProgress[existingIndex] = progress;
      } else {
        this.checklistProgress.push(progress);
      }
    }
  }

  async updateProgress(checklistProgress: ChecklistProgress) {
    if (this.enableLocaldb) {
      await this.internalDatabaseService.update(this.table, checklistProgress);
    } else {
      const index = this.checklistProgress.findIndex(p => p.Id === checklistProgress.Id);
      if (index !== -1) {
        this.checklistProgress[index] = checklistProgress;
      }
    }
  }

  async deleteProgress(id: number) {
    if (!this.enableLocaldb) {
      const index = this.checklistProgress.findIndex(p => p.Id === id);
      if (index !== -1) {
        this.checklistProgress.splice(index, 1);
      }
    } else {
      await this.internalDatabaseService.delete(this.table, id);
    }
  }

}